import type { Dispatch, SetStateAction } from "react";
import { useMemo, useRef } from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import styled, { css, useTheme } from "styled-components/macro";
import useSWR from "swr";

import { PageHeader, PageTitle } from "../../../layout/portalPageLayout";
import type { SEODetail, WithPagination } from "../../../types/types";

import { ErrorPlaceholder } from "../../../components/Error";
import type { ErrorWithStatus } from "../../../util/util";
import {
  PIM_PRODUCT_DETAILS_PORTFOLIO_TAB,
  useMediaQueries,
  useStoreState,
} from "../../../util/util";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { helmetJsonLdProp } from "react-schemaorg";
import type { Product } from "schema-dts";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { Auth } from "../../../components/Auth";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { Modal } from "../../../components/Modal/Modal";
import { SEOHelmet } from "../../../components/SEOHelmet/SEOHelmet";
import { endpoints } from "../../../endpoints";
import type {
  AttributeSchema,
  AttributeTemplateSchema,
  PIMProduct,
  PricingPriceTierSchema,
  ProductListPriceSchema,
} from "../../../types/types.PIM";
import { useRoutePath } from "../../../util/Routing";
import { ProductHeaderActions } from "../../SharedPages/ProductDetailPage/ProductDetailPage";

import type { ProductMutate } from "../../SharedPages/ProductDetailPage/ProductDetailPage";
import { ProductTabs } from "../../SharedPages/ProductDetailPage/util";
import { ProductDetailsTransactionsSection } from "./ProductDetailsTransactionsButtons";
import {
  Breadcrumb,
  FullwidthPageWrapper,
  ProductSidebar,
  ProductSidebarContent,
  ProductSidebarWrapper,
} from "../../../components/ProductsLayout/SharedProductsLayout";
import {
  PROFILE_DETAIL_CLASSES,
  profileDetailCardContainerStyles,
} from "../../../layout/shared/ProfileDetailLayout";
import { screenSize } from "../../../theme";
import { LinkUnstyled } from "../../../components/LinkUnstyled/LinkUnstyled";
import { ProductGroupCard } from "../../SharedPages/ProductDetailPage/ProductOverview/ProductGroupCard";
import { HeaderContainer } from "../../../components/Layout/Layout";
import {
  ImageWrapper,
  MasterVariantBadge,
  PlaceholderImage,
  PlaceholderLogo,
} from "../../../components/CardProduct/CardProduct";
import { useDocumentAvailability } from "../../SharedPages/ProductDetailPage/hooks/useDocumentAvailability";
import { useRelatedEntities } from "../../../components/ObjectReference/RelatedEntitiesTab";

const ProductHeader = styled(PageHeader)<{
  pageLayout: string;
  isXlScreen: boolean;
  productBackground: string;
}>`
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
  top: 0;
  z-index: 3;
  ${({ pageLayout, isXlScreen, productBackground }) =>
    pageLayout === "two_column" &&
    isXlScreen &&
    css`
      background-color: ${productBackground};
    `}
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
  @media (${screenSize.small}) {
    position: relative;
  }
`;

const ProductHeaderImage = styled.img`
  min-width: 100px;
  max-width: 200px;
  max-height: 150px;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: 1px;
  align-self: center;
  cursor: pointer;
  @media (${screenSize.small}) {
    align-self: flex-start;
  }
`;

const ProductHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  padding: 0;
  h1 {
    margin: ${({ theme }) => theme.resolve("spacing_none")} 5px
      ${({ theme }) => theme.resolve("spacing_xxs")} 0;
  }
  button {
    white-space: nowrap;
  }
`;

const ProductHeaderInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  @media (${screenSize.small}) {
    flex-direction: column;
  }
`;

const ContentContainer = styled.div<{
  pageContainerSettings: string;
  productBackground: string;
  headerHeight: number;
  pageLayout: string;
}>`
  flex: 1;
  background-color: ${({ theme, pageContainerSettings, productBackground }) =>
    pageContainerSettings === "no_containers" || !productBackground
      ? theme.resolve("color_bg_surface_product")
      : productBackground};
  [class*="TabList"] {
    padding: ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_xl")} 0;
    max-width: 100%;
    width: 100%;
    z-index: 3;
    position: sticky;
    background: ${({ pageLayout, productBackground }) =>
      pageLayout === "two_column"
        ? productBackground
        : "var(--color_neutral_white)"};
    top: 0px;
    ${({ pageLayout, productBackground }) =>
      pageLayout === "two_column" &&
      css`
        padding: ${({ theme }) => theme.resolve("spacing_xs")}
          ${({ theme }) => theme.resolve("spacing_xl")};
        background-color: ${productBackground};
        border-bottom: 0 !important;
        button {
          padding: ${({ theme }) => theme.resolve("spacing_xs")}
            ${({ theme }) => theme.resolve("spacing_lg")} !important;
          border-bottom: 0;
          margin-right: ${({ theme }) => theme.resolve("spacing_xxs")};
          background-color: var(--color_neutral_white);
          z-index: 4;
          font-size: ${({ theme }) => theme.resolve("font_size_md")};
          font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
          &[data-selected] {
            background-color: ${({ theme }) =>
              theme.resolve("color_brand_primary")};
            color: ${({ theme }) => theme.resolve("color_neutral_white")};
            font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
          }
          &:first-child {
            margin-left: ${({ theme }) => theme.resolve("spacing_xs")};
          }
          &:last-child {
            margin-right: ${({ theme }) => theme.resolve("spacing_xs")};
          }
        }
        &::before {
          content: "";
          position: absolute;
          bottom: 0;
          left: ${({ theme }) => theme.resolve("spacing_xl")};
          top: 0;
          right: ${({ theme }) => theme.resolve("spacing_xl")};
          width: calc(
            100% - ${({ theme }) => theme.resolve("spacing_4xl")} - 2px
          );
          background: var(--color_neutral_white);
          z-index: 1;
          border: ${({ theme }) => theme.resolve("border_md")} solid
            ${({ theme }) => theme.resolve("color_border_lighter")};
        }
      `}
  }
  div[data-reach-tabs] {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 0 !important;
  }
  div[data-reach-tab-panels] {
    flex: 1;
    min-height: 100%;
    background-color: ${({ theme, pageContainerSettings, productBackground }) =>
      pageContainerSettings === "no_containers" || !productBackground
        ? theme.resolve("color_bg_surface_product")
        : productBackground};
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`};
  }
  div[data-reach-tab-panel] {
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`
        : 0};
    border-radius: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? theme.resolve("radius_productDetail_card")
        : 0};
    border: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? `${theme.resolve("border_md")} solid ${theme.resolve(
            "color_border_lighter"
          )}`
        : "none"};
    background-color: ${({ pageContainerSettings }) =>
      pageContainerSettings === "single_container" ? "#fff" : "none"};
  }
  ${profileDetailCardContainerStyles}
`;

const ModalWrapper = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface IProductDetails {
  product: PIMProduct | undefined;
  mutateProduct: ProductMutate;
  error: ErrorWithStatus | undefined;
  updateListHeight?: Dispatch<SetStateAction<string | undefined>>;
  setbackToProductList?: Dispatch<SetStateAction<boolean>>;
  can_set_tab_query?: boolean;
}
/**
 * Portfolio Product detail page
 */
export function PIMProductDetailsPortfolio({
  product,
  mutateProduct,
  error,
  updateListHeight,
  can_set_tab_query = true,
  setbackToProductList,
}: IProductDetails) {
  const { hasPermission, roleIsGuest, roleIsSomeKindOfBuyer, user } =
    useContext(Auth);

  const { t } = useTranslation();
  const { storefront_metadata, tenant_id, edition, storefront_id } =
    useStoreState();
  const pageContainerSettings =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.container_settings?.value || "no_containers";
  const { pathname } = useLocation();
  const history = useHistory();
  const { storePath } = useRoutePath();
  const portfolioView = sessionStorage.getItem("portfolioView");
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  const [fullSizeProductImage, setFullSizeProductImage] = useState(false);
  const [query, setQuery] = useQueryParams({
    tab: NumberParam,
    documentType: StringParam,
  });

  const theme = useTheme();

  const [currentTab, setCurrentTab] = useState(0);

  const [headerHeight, setHeaderHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const pageLayout =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.product_detail_layout?.value || "single_column";

  const { hasAssetsOrExternalLinks, isLoading: isDocumentAvailabilityLoading } =
    useDocumentAvailability("product", product?.id);

  const targetObjectIdForRelatedEntities = product
    ? ["staged", "unpublished_staged"].includes(product.display_status)
      ? product.published_product_id
      : product.id
    : undefined;

  const { data: relatedEntities, error: relatedEntitiesError } =
    useRelatedEntities(
      tenant_id,
      "product",
      targetObjectIdForRelatedEntities,
      storefront_metadata.object_reference_enabled
    );

  const isRelatedEntitiesLoading = Boolean(
    storefront_metadata.object_reference_enabled &&
      targetObjectIdForRelatedEntities &&
      relatedEntities === undefined &&
      !relatedEntitiesError
  );

  const { isXlScreen, isLargeScreen } = useMediaQueries();
  const { data: product_list_prices, error: list_prices_error } = useSWR<
    WithPagination<{ data: ProductListPriceSchema[] }>
  >(
    product?.id
      ? endpoints.v2_tenants_tenant_id_products_product_id_list_prices(
          tenant_id,
          product.id
        )
      : null
  );

  const pricing_price_tiers_params = useMemo(() => {
    const params = new URLSearchParams();
    if (user) {
      params.append("buyer_id", user.tenant_id);
    }
    return params.toString();
  }, [user]);

  const { data: pricing_price_tiers, error: price_tiers_error } = useSWR<
    WithPagination<{ data: PricingPriceTierSchema[] }>
  >(
    roleIsSomeKindOfBuyer && !!product
      ? `${endpoints.v2_storefronts_storefront_id_pim_products_price_tiers_pricing(
          storefront_id,
          product.id
        )}?${pricing_price_tiers_params}`
      : null
  );

  const show_pricing_tab = useMemo(() => {
    // Check if either price data is still loading
    const is_loading =
      (!product_list_prices && !list_prices_error) ||
      (roleIsSomeKindOfBuyer && !pricing_price_tiers && !price_tiers_error);

    if (is_loading) {
      return false;
    }

    // Check if list prices exist and are enabled
    const has_list_prices =
      (product_list_prices?.data.length ?? 0) > 0 &&
      storefront_metadata.enable_list_prices;

    // Check if price tiers exist
    const has_price_tiers =
      (pricing_price_tiers?.data?.flatMap(({ price_tiers }) => price_tiers)
        .length ?? 0) > 0;

    return has_list_prices || has_price_tiers;
  }, [
    list_prices_error,
    price_tiers_error,
    pricing_price_tiers,
    product_list_prices,
    roleIsSomeKindOfBuyer,
    storefront_metadata.enable_list_prices,
  ]);

  useEffect(() => {
    // we don't know the length of the tabs that the buyer will see, so when the
    // product changes reset to the first tab, because there will always be 1.
    const tab =
      query?.tab ??
      (localStorage.getItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB)
        ? Number(localStorage.getItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB))
        : 0);
    setCurrentTab(tab);
  }, [query.tab]);

  const changeTab = (index: number) => {
    if (updateListHeight) {
      updateListHeight(String(index));
    }
    setCurrentTab(index);
    if (can_set_tab_query) {
      // Clear documentType when user manually changes tabs
      setQuery({
        tab: index,
        documentType: undefined,
      });
    } else {
      localStorage.setItem(PIM_PRODUCT_DETAILS_PORTFOLIO_TAB, `${index}`);
    }
  };

  const handleDocumentTypeTabChange = useCallback(
    (index: number) => {
      setCurrentTab(index);
      if (can_set_tab_query) {
        setQuery({
          tab: index,
          documentType: query.documentType,
        });
      }
    },
    [can_set_tab_query, query.documentType, setQuery]
  );

  useEffect(() => {
    if (product && ref?.current) {
      setHeaderHeight(ref.current.clientHeight);
    }
  }, [product, ref]);

  useEffect(() => {
    function handleResize() {
      if (product && ref?.current) {
        setHeaderHeight(ref.current.clientHeight);
      }
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    if (roleIsSomeKindOfBuyer && !product) {
      if (portfolioView === "modern" || portfolioView === "classic") {
        const basePath = `${storePath}/admin/pim/products`;
        const orderId = pathname.split("/").pop();
        history.replace(`${basePath}/${orderId}`);
      }
    }
  }, [
    history,
    roleIsSomeKindOfBuyer,
    portfolioView,
    storePath,
    pathname,
    product,
  ]);

  if (!product) {
    // Product data is still loading.
    return <DelayedSpinner />;
  }

  const productCoverImage = product.cover_image?.signed_url;

  const seoDetails = (): SEODetail => {
    const seoGroupSummary = product.product_schema.groups.find(
      (group) => group.name.toLowerCase() === "seo"
    );

    const seoDataType: Record<string, string> = {};

    seoGroupSummary?.attributes?.forEach(
      ({ object_type, values }) =>
        (seoDataType[object_type] = (values?.[0]?.value as string) ?? "")
    );

    return {
      meta_description: seoDataType?.seo_meta_description ?? null,
      meta_keywords: seoDataType?.seo_meta_keywords ?? null,
      meta_title: seoDataType?.seo_meta_title ?? null,
      page_title: seoDataType?.seo_page_title ?? null,
      og_title: seoDataType?.seo_og_title ?? null,
      og_type: seoDataType?.seo_og_type ?? null,
      og_image: seoDataType?.seo_og_image ?? null,
      og_description: seoDataType?.seo_og_description ?? null,
      og_locale: seoDataType?.seo_og_locale ?? null,
      og_url: seoDataType?.seo_og_url ?? null,
    };
  };

  const get_default_tag_name = () => {
    return (
      product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          (attr) =>
            attr.object_type === "product_name" &&
            !attr.is_restricted &&
            !attr.is_not_applicable
        ) ?? null
    );
  };

  const get_default_tag_description = () => {
    return (
      product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          (attr) =>
            attr.object_type === "product_description" &&
            !attr.is_not_applicable &&
            !attr.is_restricted
        ) ?? null
    );
  };

  const get_property_value = (attribute: AttributeSchema | null) => {
    const attribute_value = product.product_schema.groups
      .flatMap(({ attributes }) => attributes)
      .find(
        (attr) =>
          attr.id === attribute?.id &&
          !attribute?.is_not_applicable &&
          !attribute?.is_restricted
      );
    if (attribute_value) {
      if (
        attribute_value.input_type === "multi_select" &&
        attribute_value.values?.length
      ) {
        return attribute_value.values
          .map((val) => val.value as string)
          .join(", ");
      }
      const val = attribute_value.values?.[0]?.value;
      switch (typeof val) {
        case "string":
          return val as string;
        case "boolean":
          return val ? "Yes" : "No";
        default:
          return "";
      }
    }
    return "";
  };

  const get_product_schema = (
    schema_tag: AttributeTemplateSchema["schema_tags"]
  ) => {
    return helmetJsonLdProp<Product>({
      "@context": "https://schema.org",
      "@type": "Product",
      name: get_property_value(schema_tag?.tag_name_attribute ?? null) ?? "",
      description:
        get_property_value(schema_tag?.tag_description_attribute ?? null) ?? "",
      url: `${window.location.origin}${storePath}/product/${product.slug}`,
    });
  };

  if (error) {
    return (
      <ErrorPlaceholder
        message={error.message ?? t("There was an error loading the product.")}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
        width: "100%",
      }}
    >
      {product.product_schema.show_schema_tag && edition !== "pim" ? (
        <Helmet
          script={[
            get_product_schema(
              product.product_schema.schema_tags ?? {
                tenant_id,
                tag_name_attribute: get_default_tag_name(),
                tag_description_attribute: get_default_tag_description(),
              }
            ),
          ]}
        >
          <meta property="og:title" content={product.name} />
          {product.image_url && (
            <meta property="og:image" content={product.image_url} />
          )}
          <meta
            property="og:description"
            content={`Check out ${product.name} ${
              product.produced_by ? `by ${product.produced_by}` : ``
            } 
    on the ${storefront_metadata.tenant_name} portfolio`}
          />
        </Helmet>
      ) : null}

      {pageLayout === "two_column" && isXlScreen && (
        <ProductSidebarWrapper>
          <ProductSidebar>
            <ProductSidebarContent>
              <ProductHeader
                style={{ backgroundColor: "transparent", padding: 0 }}
                pageLayout={pageLayout}
                isXlScreen={isXlScreen}
                productBackground={
                  storefront_metadata.theme_object.designTokens?.productDetail
                    ?.color_bg_surface_product?.value || "#fff"
                }
              >
                <ImageWrapper>
                  {productCoverImage ? (
                    <img
                      src={productCoverImage}
                      alt={product.name || "Product"}
                      onClick={() => setShowProductImageModal(true)}
                    />
                  ) : (
                    <PlaceholderImage>
                      <PlaceholderLogo
                        src={theme.portalLogoUrl}
                        alt="Company Logo"
                      />
                    </PlaceholderImage>
                  )}
                  <MasterVariantBadge
                    pim_product_type={product.pim_product_type ?? "standard"}
                  />
                </ImageWrapper>
                <HeaderContainer>
                  <PageTitle style={{ fontSize: "var(--font_size_2xl)" }}>
                    {product.name}
                  </PageTitle>

                  <ProductHeaderActions>
                    <ProductDetailsTransactionsSection
                      product={product}
                      setbackToProductList={setbackToProductList}
                    />
                  </ProductHeaderActions>
                </HeaderContainer>
              </ProductHeader>
              <div className={PROFILE_DETAIL_CLASSES.detailsSection}>
                <ProductGroupCard
                  product={product}
                  group={
                    product.product_schema.groups.find(
                      (group) => group.name.toLowerCase() === "general"
                    ) ?? product.product_schema.groups[0]
                  }
                  key={
                    product.product_schema.groups.find(
                      (group) => group.name.toLowerCase() === "general"
                    )?.id ?? product.product_schema.groups[0].id
                  }
                  mutateProduct={mutateProduct}
                  isPortfolio={true}
                  replaceProductId={() => Promise.resolve()}
                  isChangeRequestEnabled={
                    storefront_metadata.enable_change_requests
                  }
                />
              </div>
            </ProductSidebarContent>
          </ProductSidebar>
        </ProductSidebarWrapper>
      )}
      <FullwidthPageWrapper style={{ position: "relative", width: "100%" }}>
        {seoDetails() && (
          <SEOHelmet
            seo={seoDetails()}
            extraProps={{
              slug: product.name,
              image_url: product.image_url,
            }}
          />
        )}

        <ProductHeader
          ref={ref}
          pageLayout={pageLayout}
          isXlScreen={isXlScreen}
          productBackground={
            storefront_metadata.theme_object.designTokens?.productDetail
              ?.color_bg_surface_product?.value || "#fff"
          }
        >
          <Breadcrumb isXlScreen={isXlScreen} pageLayout={pageLayout}>
            <LinkUnstyled
              to={`${storePath}/portfolio${
                sessionStorage.getItem("products_search") ?? ""
              }`}
            >
              {t("Products")}
            </LinkUnstyled>
            <div>/</div>
            <div>{product.name}</div>
          </Breadcrumb>
          {(pageLayout !== "two_column" ||
            (pageLayout === "two_column" && isLargeScreen)) && (
            <ProductHeaderInfoWrapper>
              {productCoverImage && (
                <ProductHeaderImage
                  src={productCoverImage}
                  alt={product.name}
                  onClick={() => setShowProductImageModal(true)}
                />
              )}
              <ProductHeaderContainer>
                <PageTitle>{product.name}</PageTitle>
                <ProductDetailsTransactionsSection
                  product={product}
                  setbackToProductList={setbackToProductList}
                />
              </ProductHeaderContainer>
            </ProductHeaderInfoWrapper>
          )}
        </ProductHeader>
        {product && (
          <ContentContainer
            pageContainerSettings={pageContainerSettings}
            productBackground={
              storefront_metadata.theme_object.designTokens?.productDetail
                ?.color_bg_surface_product?.value || "#fff"
            }
            headerHeight={headerHeight}
            pageLayout={pageLayout}
          >
            <ProductTabs
              product={product}
              t={t}
              mutate={mutateProduct}
              hasPermission={hasPermission}
              replaceProductId={() => Promise.resolve()}
              index={currentTab}
              onChange={changeTab}
              tabsKey={product.id}
              tabsStyle={{ marginTop: "8px" }}
              isPortfolio={true}
              roleIsGuest={roleIsGuest}
              roleIsBuyer={roleIsSomeKindOfBuyer}
              show_pricing_tab={show_pricing_tab ?? false}
              hasAssetsOrExternalLinks={hasAssetsOrExternalLinks}
              isDocumentAvailabilityLoading={isDocumentAvailabilityLoading}
              isRelatedEntitiesLoading={isRelatedEntitiesLoading}
              onDocumentTypeTabChange={handleDocumentTypeTabChange}
              isShowTableWithBulkAction={true}
              relatedEntities={relatedEntities}
              isShowGridAssetWithBulkAction={true}
              documentType={query.documentType || undefined}
            />
          </ContentContainer>
        )}

        {showProductImageModal && productCoverImage && (
          <Modal
            show={showProductImageModal}
            overlay={true}
            closeModal={() => setShowProductImageModal(false)}
            modalWidth="100%"
          >
            <ModalWrapper>
              <img
                src={productCoverImage}
                alt={product.name}
                style={{
                  maxWidth: fullSizeProductImage ? "auto" : "100%",
                  maxHeight: fullSizeProductImage ? "auto" : "100%",
                  cursor: fullSizeProductImage ? "zoom-out" : "zoom-in",
                }}
                onClick={() => setFullSizeProductImage((current) => !current)}
              />
            </ModalWrapper>
          </Modal>
        )}
      </FullwidthPageWrapper>
    </div>
  );
}
